import React, { useCallback, useEffect, useRef, useState } from 'react';
import { dataLayer } from '@weatherbug/common';

import { reduxConnect } from '../../../common';
import { MediaWidgetView } from '../../common/mediaWidgets/mediaWidgetView';
import { NoWidgetCamData } from '../sharedComponents/noWidgetCamData.jsx';
import { LazyLoadImg } from '../../../common/utilities/lazyLoadImg';
import { mediaWidgetImageStyle } from '../../common/mediaWidgets/mediaImageWidgetStyle';
import { IStoreState } from '../../common/interfaces/types';
import { useIntersectionObserver } from '../../common/useIntersectionObserver';
import { translate } from '../../../common/translation/translate';
import { getWidgetHeaderText } from '../../../common/utilities/getWidgetHeaderUtil';
import { WidgetErrorUI } from '../../widgetErrorUI/WidgetErrorUI';
import { isDataError } from '../../common/utilities/isDataError';

const route = '/weather-camera/';
const includeImagesFromWeatherCams = 1;
const weatherCamRadius = 1000;
const numberOfWeatherCamStations = 1;
const isWeatherCamWidget = true;

const { getWeatherCams } = dataLayer.actions;

interface WeatherCamsWidgetProps {
    latitude: number;
    longitude: number;
    slugName: string;
    authId: string;
    secret: string;
    weatherCamBaseUrl: string;
    getWeatherCams: typeof getWeatherCams;
    imageUrl: string;
    nonUSLoc: boolean;
    weatherCamDataLoadError: boolean;
    cityName: string;
    stateAbbreviation: string;
    enableWeatherCamerasPageOnWeb2: boolean;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { weatherCam, appState } =  storeState || {};

    const { authId, secret, contentMgmtBaseUrl, user, enableWeatherCamerasPageOnWeb2 } = appState?.appState || {};
    const {
        SlugName: slugName, 
        Latitude: latitude, 
        Longitude: longitude,
        CityName: cityName, 
        TerritoryAbbr: stateAbbreviation  } = user?.ActiveLocation?.Locality || {};
    const imageUrl = weatherCam?.list?.[0]?.Thumbnail ?? '';
    const nonUSLoc = user?.ActiveLocation?.Locality?.CountryShortName !== 'US';
    const { lastError } = weatherCam ?? {};
    const weatherCamDataLoadError = isDataError(lastError);

    return {
        latitude,
        longitude,
        slugName,
        cityName,
        stateAbbreviation,
        imageUrl,
        authId,
        secret,
        weatherCamDataLoadError,
        weatherCamBaseUrl: contentMgmtBaseUrl,
        nonUSLoc,
        enableWeatherCamerasPageOnWeb2
    };
};

const mapDispatchToProps = {
    getWeatherCams
};

export const WeatherCamsWidget = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({
    longitude,
    slugName='',
    cityName,
    stateAbbreviation,
    authId,
    secret,
    weatherCamDataLoadError,
    weatherCamBaseUrl,
    getWeatherCams,
    enableWeatherCamerasPageOnWeb2,
    imageUrl,
    nonUSLoc, 
    latitude }: WeatherCamsWidgetProps) => {

    const [errorLoadingImage, setErrorLoadingImage] = useState<boolean>(false);
    const widgetRef = useRef<HTMLAnchorElement | null>(null);

    const isWeatherCamsWidgetVisible = useIntersectionObserver(widgetRef, '330px');

    useEffect(() => {
        try {
            if (Number.isFinite(latitude)
                && Number.isFinite(longitude)
                && weatherCamBaseUrl
                && authId
                && isWeatherCamsWidgetVisible
                && secret) {
                getWeatherCams(
                    latitude,
                    longitude,
                    weatherCamRadius,
                    numberOfWeatherCamStations,
                    includeImagesFromWeatherCams,
                    weatherCamBaseUrl,
                    authId,
                    secret);
            }
        } catch (error) {
            console.error('Failed to load weather cams widget data', error);
        }
    }, [latitude, longitude, authId, secret, weatherCamBaseUrl, isWeatherCamsWidgetVisible]);

    const onImgError = useCallback(() => {
        setErrorLoadingImage(true);
    }, []);

    const linkURL = `${route}${slugName}`;

    const widgetTitle = translate('weatherCameras');
    const widgetHeaderText = getWidgetHeaderText({ widgetTitle, cityName, stateAbbreviation });

    return (
        <MediaWidgetView
            enableWeatherCamerasPageOnWeb2={enableWeatherCamerasPageOnWeb2}
            isErrorView={errorLoadingImage}
            routeToPage={linkURL}
            widgetHeaderText={widgetHeaderText}
            showArrowIcon={true}
            widgetRef={widgetRef}
            bodyChildren={
                !weatherCamDataLoadError 
                    ? imageUrl && !errorLoadingImage
                        ? <LazyLoadImg 
                            style={mediaWidgetImageStyle}
                            width={mediaWidgetImageStyle.width}
                            height={mediaWidgetImageStyle.height}
                            alt='WeatherBug Traffic Cameras widget'
                            src={imageUrl}
                            handleImageLoadError={onImgError} />
                        : <NoWidgetCamData 
                            noAvailableCam={nonUSLoc} 
                            isWeatherCamWidget={isWeatherCamWidget} />
                    : <WidgetErrorUI />
            }
        />
    );
});

export { WeatherCamsWidget as default };
